class UpsellQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.variantInput = this.querySelector('[data-upsell-variant-id]');
    this.submitButton = this.querySelector('[data-upsell-submit]');
    this.errorElement = this.querySelector('[data-upsell-error]');
    this.priceWrapper = this.querySelector('[data-upsell-price-wrapper]');
    this.variantData = JSON.parse(this.querySelector('[data-upsell-variants]')?.textContent || '[]');

    this.preselectVariant();
    this.addEventListener('change', this.handleOptionChange.bind(this));
    this.form?.addEventListener('submit', this.handleSubmit.bind(this));
  }

  preselectVariant() {
    const selectedVariantId = Number(this.dataset.selectedVariantId);
    const preselectedColor = (this.dataset.preselectedColor || '').trim().toLowerCase();
    const selectedVariant = selectedVariantId
      ? this.variantData.find((variant) => Number(variant.id) === selectedVariantId)
      : null;
    const matchedVariant = preselectedColor
      ? this.variantData.find((variant) => (variant.options[0] || '').trim().toLowerCase() === preselectedColor && variant.available)
      : null;

    this.selectVariant(selectedVariant || matchedVariant || this.variantData.find((variant) => variant.available) || this.variantData[0]);
  }

  selectVariant(variant) {
    if (!variant) {
      return;
    }

    variant.options.forEach((value, index) => {
      const input = this.querySelector(`input[data-option-position="${index + 1}"][value="${CSS.escape(value)}"]`);

      if (input) {
        input.checked = true;
        this.updateOptionSelectedLabel(input);
      }
    });

    this.updateVariant(variant);
  }

  handleOptionChange(event) {
    if (!event.target.matches('[data-option-position]')) {
      return;
    }

    this.updateOptionSelectedLabel(event.target);

    const selectedOptions = {};
    const selectedOptionInputs = this.querySelectorAll('[data-option-position]:checked');

    for (const input of selectedOptionInputs) {
      selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
    }

    selectedOptions[Number(event.target.dataset.optionPosition) - 1] = event.target.value;

    const variant = this.variantData.find((item) => {
      return Object.entries(selectedOptions).every(([index, value]) => item.options[Number(index)] === value);
    });

    this.updateVariant(variant);
  }

  updateOptionSelectedLabel(input) {
    let optionFieldset = input.closest('.upsell-quick-add-modal__option');

    if (!optionFieldset) {
      const popover = input.closest('x-popover');
      const popoverId = popover?.id || '';
      const popoverButton = popoverId ? document.querySelector(`[aria-controls="${CSS.escape(popoverId)}"]`) : null;
      optionFieldset = popoverButton?.closest('.upsell-quick-add-modal__option');
    }

    const selectedLabel = optionFieldset?.querySelector('[data-upsell-option-selected]');

    if (selectedLabel) {
      selectedLabel.textContent = input.value;
    }
  }

  updateVariant(variant) {
    if (!variant) {
      if (this.variantInput) {
        this.variantInput.value = '';
      }

      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.textContent = this.submitButton.dataset.unavailableText;
      }

      return;
    }

    this.updateMedia(variant.featured_media_id);
    this.updatePrice(variant.id);

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.submitButton) {
      this.submitButton.disabled = !variant.available;
      this.submitButton.textContent = variant.available ? this.submitButton.dataset.availableText : this.submitButton.dataset.soldOutText;
    }
  }

  updatePrice(variantId) {
    if (!this.priceWrapper || !variantId) {
      return;
    }

    const template = this.querySelector(`[data-upsell-price-template="${CSS.escape(String(variantId))}"]`);

    if (!template) {
      return;
    }

    this.priceWrapper.replaceChildren(template.content.cloneNode(true));
  }

  updateMedia(mediaId) {
    if (!mediaId) {
      return;
    }

    const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
    slide?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.form || !this.form.checkValidity()) {
      this.form?.reportValidity();
      return;
    }

    this.setBusy(true);
    this.setError('');

    try {
      const formData = new FormData(this.form);
      const shouldOpenCart = this.dataset.openCart === 'true';

      if (shouldOpenCart) {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        formData.set('sections', sections.join(','));
        formData.set('sections_url', `${Shopify.routes.root}variants/${this.variantInput.value}`);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || window.themeVariables.strings.productFormErrorMessage);
      }

      const modal = this.closest('x-modal');
      modal?.hide();

      if (shouldOpenCart) {
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = responseJson.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      this.setError(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(isBusy) {
    this.submitButton?.toggleAttribute('aria-busy', isBusy);
  }

  setError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message;
    this.errorElement.hidden = message === '';
  }
}

if (!window.customElements.get('upsell-quick-add')) {
  window.customElements.define('upsell-quick-add', UpsellQuickAdd);
}

// Handle BYO popover choices before the mobile popover can stop the click from bubbling.
document.addEventListener('click', (event) => {
  const optionLabel = event.target.closest('x-popover .popover__value-option');

  if (!optionLabel) {
    return;
  }

  const popover = optionLabel.closest('x-popover');
  const popoverId = popover?.id || '';
  const popoverButton = popoverId ? document.querySelector(`[aria-controls="${CSS.escape(popoverId)}"]`) : null;
  const quickAdd = popoverButton?.closest('.byo-quick-add-modal upsell-quick-add');
  const optionInput = optionLabel.querySelector('input[data-option-position]');

  if (!quickAdd || !optionInput) {
    return;
  }

  event.preventDefault();
  optionInput.checked = true;
  quickAdd.handleOptionChange({ target: optionInput });

  if (popover && typeof popover.hide === 'function') {
    popover.hide();
  }
}, true);

let upsellSwatchScopeIndex = 0;

function scopeUpsellSwatches(root = document) {
  const fieldsets = root.querySelectorAll('.upsell-products__info fieldset');

  fieldsets.forEach((fieldset) => {
    if (fieldset.dataset.upsellSwatchScoped === 'true') {
      return;
    }

    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"][name]'));

    if (!inputs.length) {
      return;
    }

    const selectedInput = inputs.find((input) => input.hasAttribute('checked')) || inputs.find((input) => input.checked) || inputs[0];
    const scopeId = upsellSwatchScopeIndex;
    const scopedName = `${inputs[0].name}--scope-${scopeId}`;
    upsellSwatchScopeIndex += 1;

    inputs.forEach((input, index) => {
      const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const scopedId = `${input.id}--scope-${scopeId}-${index}`;

      input.name = scopedName;
      input.id = scopedId;
      input.checked = input === selectedInput;

      if (label) {
        label.setAttribute('for', scopedId);
      }
    });

    fieldset.dataset.upsellSwatchScoped = 'true';
  });
}

function observeUpsellSwatches() {
  scopeUpsellSwatches();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          scopeUpsellSwatches(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeUpsellSwatches, { once: true });
} else {
  observeUpsellSwatches();
}

document.addEventListener('change', (event) => {
  const input = event.target.closest('.upsell-products__info input[type="radio"][data-variant-media]');

  if (!input) {
    return;
  }

  const item = input.closest('.upsell-products__item');
  const image = item?.querySelector('.upsell-products__image');
  const links = item?.querySelectorAll('a[href*="/products/"]');

  if (!item || !image) {
    return;
  }

  try {
    const media = JSON.parse(input.dataset.variantMedia);
    const src = media.preview_image?.src || media.src;

    if (src) {
      image.src = src;
      image.srcset = '';
    }

    if (input.dataset.variantId) {
      links?.forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', input.dataset.variantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
  } catch (error) {
    console.error('Error updating upsell swatch media:', error);
  }
});
